import moment from "moment-timezone";

import { SidePanelShell } from "@/components/ui/SidePanelShell";
import { CommonDestinationTimezone } from "@/lib/utils";
import type {
  PaymentRemittanceListRow,
  PaymentRemittanceListSourcePdf,
} from "@/lib/api/paymentRemittancePdfs";

interface PaymentRemittanceSidePanelProps {
  batch: PaymentRemittanceListRow;
  onClose: () => void;
}

/**
 * Side panel for a single payment-remittance batch. Lays out the
 * source PDFs as small cards (vertical stack) instead of a wide table,
 * since the panel is narrow.
 */
export function PaymentRemittanceSidePanel({
  batch,
  onClose,
}: PaymentRemittanceSidePanelProps) {
  return (
    <SidePanelShell title="Batch PDFs" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <BatchHeader batch={batch} />

        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Source PDFs ({batch.source_pdfs.length})
          </div>
          {batch.source_pdfs.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No source PDFs.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {batch.source_pdfs.map((sp: PaymentRemittanceListSourcePdf) => (
                <SourcePdfCard key={sp.id} pdf={sp} />
              ))}
            </div>
          )}
        </div>
      </div>
    </SidePanelShell>
  );
}

function BatchHeader({ batch }: { batch: PaymentRemittanceListRow }) {
  const period = moment.utc(batch.period_date).format("YYYY-MM");
  const created = moment
    .tz(batch.created_at, CommonDestinationTimezone)
    .format("YYYY-MM-DD HH:mm");

  return (
    <div className="rounded-md border bg-muted/30 p-3 flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Period
        </div>
        <div className="font-mono text-xs">{period}</div>
      </div>
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Status
        </div>
        <div className="font-mono text-xs">{batch.status}</div>
      </div>
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Created
        </div>
        <div className="font-mono text-xs">{created}</div>
      </div>
    </div>
  );
}

function SourcePdfCard({ pdf }: { pdf: PaymentRemittanceListSourcePdf }) {
  return (
    <div className="rounded-md border bg-card p-3 flex flex-col gap-2">
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="font-mono text-xs break-all flex-1">
          {pdf.original_filename}
        </div>
        <div className="text-[10px] text-muted-foreground shrink-0">
          #{pdf.upload_order_index}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div className="text-muted-foreground">Upload</div>
        <div className="font-mono text-right">{pdf.upload_status}</div>

        <div className="text-muted-foreground">Processing</div>
        <div className="font-mono text-right">{pdf.processing_status}</div>

        <div className="text-muted-foreground">Pages (total)</div>
        <div className="font-mono text-right">
          {pdf.total_page_count ?? "—"}
        </div>

        <div className="text-muted-foreground">Classified</div>
        <div className="font-mono text-right">{pdf.classified_page_count}</div>

        <div className="text-muted-foreground">Failed</div>
        <div className="font-mono text-right">{pdf.failed_page_count}</div>
      </div>
    </div>
  );
}
